/* eslint-disable @typescript-eslint/ban-ts-comment */

import { useEffect } from 'react';
import { api } from 'services/api';

// ==========================================================
// Funções de 'serviço'
// ==========================================================

function loadFreshchatScript() {
  //   console.log("Freshchat: carregando script...");
  let helpdeskScript = document.getElementById('freshDesk-script');
  const existing = document.getElementById('freshDesk-widget');

  if (existing) {
    existing.remove();
  }
  if (!helpdeskScript) {
    // Cria o elemento script base se não existir
    helpdeskScript = document.createElement('script');
    helpdeskScript.id = 'freshDesk-script';
    document.head.appendChild(helpdeskScript);
  }

  const script = document.createElement('script');
  script.src = '//fw-cdn.com/12121290/4629993.js';
  script.setAttribute('chat', 'true');
  script.id = 'freshDesk-widget';
  helpdeskScript.insertAdjacentElement('afterend', script);
}

function getRestoreId(email: string | null) {
  if (!email) {
    return Promise.resolve(null);
  }

  return api
    .get('/login/fresh-desk?email=' + email, {
    })
    .then(function (res) {
      return res.data.data.restoreId;
    })
    .catch(function (error) {
      console.error('Freshchat Error (getRestoreId):', error);
      return null;
    });
}

function saveRestoreId(restoreId: string | null, email: string | null, agencyId: string | null) {
  if (!restoreId || !email) {
    return;
  }

  api
    .post('/login/fresh-desk', {
      restoreId: restoreId,
      email: email,
      agencyId: agencyId,
    })
    .catch(function (error) {
      console.error('Freshchat Error (saveRestoreId):', error);
    });
}

const freshEnumProfile: Record<number, string> = {
  1: 'Diretor',
  2: 'Supervisor',
  4: 'Auxiliar Adm.',
  8: 'Telefonista/Atendente',
  3: 'Corretor',
  9: 'Gerente (Líder Equipe)',
};

// ==========================================================
// Lógica de Inicialização (Refatorada)
// ==========================================================

/**
 * Função principal que busca dados e identifica o usuário no chat.
 * (Versão corrigida que lida com usuários novos)
 */
function identificarUsuarioNoChat(freshUserEmail: string | null, freshRestoreId: string | null) {
  if (!window.fcWidget) {
    return;
  }

  window.fcWidget.user.isExists().then(function (existsResp: any) {
    const userExists = existsResp && existsResp.data === true;

    if (userExists) {
      // --- CENÁRIO 1: USUÁRIO EXISTE ---
      // O widget já tem um usuário. Vamos checar se é o correto.
      window.fcWidget.user.get().then(function (resp: any) {
        const dadosUsuarioAtual = resp && resp.data;
        const currentExternal =
          (dadosUsuarioAtual && (dadosUsuarioAtual.externalId || dadosUsuarioAtual.email)) || null;
        const isDifferentUser = !!(currentExternal && currentExternal !== freshUserEmail);

        if (isDifferentUser) {
          // É um usuário DIFERENTE. Limpa a sessão antiga.
          //   console.warn("Freshchat: Usuário diferente detectado. Limpando sessão.");
          window.fcWidget.user.clear().then(function () {
            // Registra o novo usuário
            registrarOuAtualizarUsuario(freshUserEmail, freshRestoreId);
          });
        } else {
          // É o MESMO usuário. Apenas atualiza as propriedades.
          //   console.log("Freshchat: Mesmo usuário. Atualizando propriedades.");
          registrarOuAtualizarUsuario(freshUserEmail, freshRestoreId);
        }
        const userFreshDesk = window.sessionStorage.getItem('userFreshDesk');
        const user = JSON.parse(userFreshDesk ?? '');
        // Se não tínhamos um freshRestoreId, mas o widget retornou um, salvamos
        if (
          resp.status === 200 &&
          !freshRestoreId &&
          dadosUsuarioAtual &&
          dadosUsuarioAtual.restoreId && user.agency_id
        ) {
          saveRestoreId(dadosUsuarioAtual.restoreId, freshUserEmail, user.agency_id);
        }
      }).catch(function () {
        // Falha ao "pegar" um usuário que "existe".
        //   console.error("Freshchat: Falha ao get() usuário existente. Tentando registrar de qualquer forma.", getError);
        registrarOuAtualizarUsuario(freshUserEmail, freshRestoreId);
      });
    } else {
      // --- CENÁRIO 2: USUÁRIO NÃO EXISTE ---
      // Este é o caso do "usuário novo" (ou sessão limpa).
      // Não há erro 401, apenas registramos nosso usuário.
      //   console.log("Freshchat: Nenhum usuário existe no widget. Registrando novo.");
      registrarOuAtualizarUsuario(freshUserEmail, freshRestoreId);
    }
  }).catch(function (isExistsError: any) {
    // Falha catastrófica ao checar 'isExists'
    console.error('Freshchat: Falha crítica ao chamar user.isExists().', isExistsError);
    // Como plano B, tentamos registrar o usuário de qualquer forma.
    registrarOuAtualizarUsuario(freshUserEmail, freshRestoreId);
  });
}

/**
 * Aguarda o 'userFreshDesk' aparecer no sessionStorage
 * e então registra ou atualiza o usuário no Freshchat.
 * Tenta indefinidamente a cada 500ms.
 */
function registrarOuAtualizarUsuario(
  freshUserEmail: string | null,
  freshRestoreId: string | null,
) {
  const pollingInterval = 500; // Tentar a cada 500ms
  // console.log("Freshchat: Aguardando 'userFreshDesk' no sessionStorage...");

  const checkUserInterval = setInterval(function () {
    // <--- Sintaxe de função tradicional
    const userFreshDesk = window.sessionStorage.getItem('userFreshDesk');

    // 1. SE ENCONTROU O ITEM, EXECUTA A LÓGICA E PARA
    if (userFreshDesk) {
      // Para de tentar
      clearInterval(checkUserInterval);
      //   console.log("Freshchat: 'userFreshDesk' encontrado. Processando...");

      try {
        const user = JSON.parse(userFreshDesk);

        if (!window.fcWidget) {
          return;
        }

        // IMPORTANTE: As variáveis 'freshUserEmail' e 'freshEnumProfile'
        // devem existir no escopo global (window) para isso funcionar.
        window.fcWidget.user.setProperties({
          // Dados principais de identificação
          externalId: freshUserEmail,
          restoreId: freshRestoreId,

          // Dados do usuário
          firstName: user.name,
          email: freshUserEmail,
          phone: user.kenlo_phone,

          // Dados customizados (cf_*)
          cf_login_usuario: freshUserEmail,
          sales_account: freshUserEmail,
          cf_identificador_do_cliente: user.customer_code,
          cf_perfil: freshEnumProfile[Number(user.profile)]
      });
      } catch (e) {
        console.error(
          "Freshchat: Erro ao processar 'userFreshDesk' ou setar propriedades.",
          e,
          userFreshDesk,
        );
      }
    }
    // 2. SE NÃO ENCONTROU, O INTERVALO CONTINUA...
  }, pollingInterval);
}

export const FreshDeskLoader = () => {
  useEffect(() => {
    // Variáveis necessárias para iniciar o FreshChat
    const userFreshDesk = window.sessionStorage.getItem('userFreshDesk');
    const user = JSON.parse(userFreshDesk ?? '');
    const freshUserEmail = user.email;
    let freshRestoreId: string | null = null;

    // Config necessária antes de iniciar o script
    window.fcWidgetMessengerConfig = {
      locale: 'pt-BR',
      config: {
        headerProperty: { direction: 'ltr' },
      },
      externalId: freshUserEmail || '',
    };

    // Carregar restoreId e carregar script do FreshChat após
    getRestoreId(freshUserEmail).then(function (restoreId) {
      //   console.log("Freshchat: restoreId obtido do backend:", restoreId);
      if (restoreId) {
        freshRestoreId = restoreId;
        if (window.fcWidgetMessengerConfig) {
          window.fcWidgetMessengerConfig.restoreId = restoreId;
        }
      }
      loadFreshchatScript();
    }).catch(function () {
      loadFreshchatScript();
    });

    // Config do FreshChat para rodar ao iniciar o widget
    window.fcSettings = {
      onInit: function () {
        //   console.log("Freshchat: onInit disparou, email atual:" + freshUserEmail);
        // O widget está pronto. Agora buscamos dados e identificamos o usuário.
        identificarUsuarioNoChat(freshUserEmail, freshRestoreId);

        const userFreshDesk = window.sessionStorage.getItem('userFreshDesk');
        const user = JSON.parse(userFreshDesk ?? '');
        // Ouvir 'user:created' para salvar o restoreId de novas conversas
        window.fcWidget.on('user:created', function (resp: any) {
          const status = resp && resp.status;
          const data = resp && resp.data;
          //   console.log("Freshchat: 'user:created' disparou: " + JSON.stringify(resp.data));
          if (status === 200 && data && data.restoreId && !freshRestoreId && user.agency_id) {
            //     console.log("Freshchat: 'user:created' disparou, com restoreId " + data.restoreId);
            freshRestoreId = data.restoreId;
            saveRestoreId(data.restoreId, freshUserEmail, user.agency_id);
          }
        });
      },
    };
  }, []);

  return null;
};
